import React, { useState } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import TimelineItem from './TimelineItem';
import TimelineSkeleton from './TimelineSkeleton';
import TimelineDetailsModal from './TimelineDetailsModal';

const Timeline = ({
  items = [],
  config = {},
  loading = false,
  error = null,
  title,
  subtitle,
}) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const {
    // INFO: Default configuration
    getId = (item) => item.id,
    getDate = (item) => item.date || item.changed_at || item.created_at,
    getIcon = () => ({ component: null, color: 'grey' }),
    getTitle = (item) => item.title || 'Untitled',
    getDescription = (item) => item.description || '',
    getMetadata = () => ({ primary: '', secondary: '' }),
    renderActions = () => null,
    renderExtra = () => null,
    dateFormat = 'short',
    layout = {
      dateWidth: 200,
      lineWidth: 2,
      lineSpacing: 60,
      itemSpacing: 3,
    },
    empty = {
      message: 'No items found',
      icon: null,
    },
    loading: loadingConfig = {
      skeletonCount: 5,
      skeletonHeight: 80,
    },
    detailsModal = {
      enabled: false,
      config: {},
    },
  } = config;

  const handleOpenModal = (item) => {
    setSelectedItem(item);
    setDetailsModalOpen(true);
  };

  const handleCloseModal = () => {
    setDetailsModalOpen(false);
    setSelectedItem(null);
  };

  const formatDate = (dateString) => {
    if (typeof dateFormat === 'function') {
      return dateFormat(dateString);
    }

    const date = new Date(dateString);

    switch (dateFormat) {
      case 'short':
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      case 'long':
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
      case 'relative':
        return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
          Math.ceil((date - new Date()) / (1000 * 60 * 60 * 24)),
          'day',
        );
      default:
        return date.toLocaleDateString();
    }
  };

  if (loading) {
    return (
      <Box>
        {(title || subtitle) && (
          <Box sx={{ mb: 3 }}>
            {title && (
              <Typography variant='h6' gutterBottom>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant='body2' color='text.secondary'>
                {subtitle}
              </Typography>
            )}
          </Box>
        )}
        <TimelineSkeleton
          count={loadingConfig.skeletonCount}
          height={loadingConfig.skeletonHeight}
          dateWidth={layout.dateWidth}
        />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        {(title || subtitle) && (
          <Box sx={{ mb: 3 }}>
            {title && (
              <Typography variant='h6' gutterBottom>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant='body2' color='text.secondary'>
                {subtitle}
              </Typography>
            )}
          </Box>
        )}
        <Alert severity='error'>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {(title || subtitle) && (
        <Box sx={{ mb: 3 }}>
          {title && (
            <Typography variant='h6' gutterBottom>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      )}

      {items.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          {empty.icon && (
            <Box sx={{ mb: 2, color: 'text.secondary' }}>{empty.icon}</Box>
          )}
          <Typography variant='body1' color='text.secondary'>
            {empty.message}
          </Typography>
        </Box>
      ) : (
        <Box>
          {items.map((item, index) => {
            const isLast = index === items.length - 1;

            return (
              <TimelineItem
                key={getId(item)}
                item={item}
                isLast={isLast}
                config={{
                  getDate,
                  getIcon,
                  getTitle,
                  getDescription,
                  getMetadata,
                  renderActions,
                  renderExtra,
                  formatDate,
                  layout,
                  openModal: detailsModal.enabled ? handleOpenModal : null,
                }}
              />
            );
          })}
        </Box>
      )}

      {detailsModal.enabled && (
        <TimelineDetailsModal
          open={detailsModalOpen}
          onClose={handleCloseModal}
          item={selectedItem}
          config={detailsModal.config}
        />
      )}
    </Box>
  );
};

export default Timeline;
